import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FiDollarSign,
  FiCreditCard,
  FiArrowLeft,
  FiCheckCircle,
  FiAlertCircle,
  FiShield,
  FiLock,
} from "react-icons/fi";
import { BsWallet2, BsBank } from "react-icons/bs";
import { SeoHead } from "../utils/seo";
import {
  initializeDeposit,
  initializeKorapayDeposit,
  initializeTransactpayDeposit,
  fetchEnabledPaymentMethods,
} from "../store/slices/paymentSlice";
import { GlassCard } from "../components/ui/GlassCard";
import toast from "react-hot-toast";

export const DepositPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, enabledPaymentMethods } = useSelector(
    (state) => state.payments,
  );
  const [amount, setAmount] = useState("");
  const [selectedMethod, setSelectedMethod] = useState("korapay");
  const [step, setStep] = useState(1);

  const presetAmounts = [1000, 5000, 10000, 25000, 50000, 100000];

  useEffect(() => {
    dispatch(fetchEnabledPaymentMethods());
    if (enabledPaymentMethods.length > 0 && !selectedMethod) {
      setSelectedMethod(enabledPaymentMethods[0]);
    }
  }, [dispatch]);

  useEffect(() => {
    if (enabledPaymentMethods.length > 0 && !selectedMethod) {
      setSelectedMethod(enabledPaymentMethods[0]);
    }
  }, [enabledPaymentMethods, selectedMethod]);

  const handleAmountSelect = (presetAmount) => {
    setAmount(presetAmount.toString());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!amount || amount < 100) {
      toast.error("Minimum deposit amount is ₦100");
      return;
    }

    if (!selectedMethod) {
      toast.error("Please select a payment method");
      return;
    }

    try {
      const amountInt = parseInt(amount);

      if (selectedMethod === "paystack") {
        await dispatch(initializeDeposit(amountInt));
      } else if (selectedMethod === "korapay") {
        await dispatch(initializeKorapayDeposit(amountInt));
      } else if (selectedMethod === "transactpay") {
        await dispatch(initializeTransactpayDeposit(amountInt));
      }
    } catch (error) {
      console.error("Deposit failed:", error);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 },
  };

  const pageTitle = "Deposit Funds | Nitro Logs";
  const pageDescription =
    "Deposit funds securely into your Nitro Logs wallet using Paystack or Korapay, then buy verified social media accounts and services instantly.";
  const pageUrl =
    typeof window !== "undefined"
      ? window.location.href
      : "https://nitrologs.com/deposit";

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case "paystack":
        return <BsBank className="w-6 h-6 text-primary-500" />;
      case "korapay":
        return (
          <div className="w-6 h-6 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-xs">
            K
          </div>
        );
      case "transactpay":
        return (
          <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xs">
            T
          </div>
        );
      default:
        return <FiCreditCard className="w-6 h-6 text-primary-500" />;
    }
  };

  const getPaymentMethodName = (method) => {
    switch (method) {
      case "paystack":
        return "Paystack";
      case "korapay":
        return "Korapay";
      case "transactpay":
        return "Transactpay";
      default:
        return method;
    }
  };

  const getPaymentMethodDescription = (method) => {
    switch (method) {
      case "paystack":
        return "Pay with card, bank transfer or USSD";
      case "korapay":
        return "Fast and secure payment";
      case "transactpay":
        return "Multiple payment options available";
      default:
        return "";
    }
  };

  if (enabledPaymentMethods.length === 0) {
    return (
      <>
        <SeoHead
          title={pageTitle}
          description={pageDescription}
          keywords="deposit funds, Nitro Logs deposit, add wallet balance, Paystack deposit, Korapay deposit"
          pageUrl={pageUrl}
        />

        <div className="min-h-screen bg-linear-to-br from-luxury-50 via-white to-luxury-100 py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 text-luxury-600 hover:text-primary-600 transition-colors mb-4 text-sm"
            >
              <FiArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
            <p className="text-2xl">
              Loading payment methods, Please wait a bit...
            </p>
            {/* <GlassCard className="p-8 text-center">
              <FiAlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-luxury-900 mb-2">No Payment Methods Available</h2>
              <p className="text-luxury-600">
                Payment methods are currently unavailable. Please contact support.
              </p>
            </GlassCard> */}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SeoHead
        title={pageTitle}
        description={pageDescription}
        keywords="deposit funds, Nitro Logs deposit, add wallet balance, Paystack deposit, Korapay deposit"
        pageUrl={pageUrl}
      />

      <div className="min-h-screen bg-linear-to-br from-luxury-50 via-white to-luxury-100 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 sm:mb-8"
          >
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 text-luxury-600 hover:text-primary-600 transition-colors mb-3 sm:mb-4 text-sm"
            >
              <FiArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold text-luxury-900 mb-2">
              Deposit <span className="gradient-text">Funds</span>
            </h1>
            <p className="text-xs sm:text-sm md:text-base text-luxury-600">
              Add money to your wallet to start purchasing accounts
            </p>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="flex items-center justify-center mb-8"
          >
            {[1, 2].map((s) => (
              <React.Fragment key={s}>
                <div className={`flex items-center ${s < 2 ? "flex-1" : ""}`}>
                  <div className={`relative`}>
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        s <= step
                          ? "bg-linear-to-r from-primary-500 to-secondary-500 text-white"
                          : "bg-luxury-200 text-luxury-400"
                      }`}
                    >
                      {s < step ? <FiCheckCircle className="w-5 h-5" /> : s}
                    </div>
                  </div>
                  {s < 2 && (
                    <div
                      className={`flex-1 h-1 mx-2 ${
                        s < step
                          ? "bg-linear-to-r from-primary-500 to-secondary-500"
                          : "bg-luxury-200"
                      }`}
                    />
                  )}
                </div>
              </React.Fragment>
            ))}
          </motion.div>

          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="space-y-6"
          >
            {step === 1 && (
              <GlassCard className="p-6 sm:p-8">
                <h2 className="text-lg sm:text-xl font-semibold text-luxury-900 mb-6">
                  Enter Deposit Amount
                </h2>

                <div className="space-y-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-luxury-700 mb-2">
                      Amount (₦)
                    </label>
                    <div className="relative">
                      <FiDollarSign className="absolute left-4 top-1/2 transform -translate-y-1/2 text-luxury-400" />
                      <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 border border-luxury-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-xl sm:text-2xl font-bold"
                        placeholder="0.00"
                        min="100"
                      />
                    </div>
                    <p className="text-xs text-luxury-500 mt-2">
                      Minimum: ₦100
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-luxury-700 mb-3">
                      Quick Select
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                      {presetAmounts.map((preset) => (
                        <button
                          key={preset}
                          onClick={() => handleAmountSelect(preset)}
                          className={`p-2 sm:p-3 rounded-xl border-2 transition-all duration-300 text-sm sm:text-base font-medium ${
                            parseInt(amount) === preset
                              ? "border-primary-500 bg-primary-50 text-primary-700"
                              : "border-luxury-200 hover:border-primary-200 text-luxury-600"
                          }`}
                        >
                          ₦{preset.toLocaleString()}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={() => setStep(2)}
                    disabled={!amount || amount < 100}
                    className="w-full py-3 px-4 bg-linear-to-r from-primary-600 to-secondary-600 text-white rounded-xl font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm sm:text-base"
                  >
                    Continue to Payment Method
                  </button>
                </div>
              </GlassCard>
            )}

            {step === 2 && (
              <GlassCard className="p-6 sm:p-8">
                <h2 className="text-lg sm:text-xl font-semibold text-luxury-900 mb-6">
                  Select Payment Method
                </h2>

                <div className="space-y-4 mb-6">
                  {enabledPaymentMethods.map((method) => (
                    <label
                      key={method}
                      className={`block p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                        selectedMethod === method
                          ? "border-primary-500 bg-primary-50"
                          : "border-luxury-200 hover:border-primary-200"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <input
                            type="radio"
                            name="paymentMethod"
                            value={method}
                            checked={selectedMethod === method}
                            onChange={(e) => setSelectedMethod(e.target.value)}
                            className="w-5 h-5 text-primary-500"
                          />
                          {getPaymentMethodIcon(method)}
                          <div>
                            <p className="font-semibold text-sm sm:text-base text-luxury-900">
                              {getPaymentMethodName(method)}
                            </p>
                            <p className="text-xs sm:text-sm text-luxury-500">
                              {getPaymentMethodDescription(method)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>

                <div className="bg-luxury-100 p-4 sm:p-6 rounded-xl mb-6">
                  <h3 className="font-semibold text-xs sm:text-sm text-luxury-900 mb-4">
                    Deposit Summary
                  </h3>
                  <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                    <div className="flex justify-between">
                      <span className="text-luxury-600">Amount:</span>
                      <span className="font-medium text-luxury-900">
                        ₦{parseInt(amount).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-luxury-600">Payment Method:</span>
                      <span className="font-medium text-luxury-900">
                        {getPaymentMethodName(selectedMethod)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-luxury-600">Fee:</span>
                      <span className="font-medium text-luxury-900">Free</span>
                    </div>
                    <div className="flex justify-between pt-2 sm:pt-3 border-t border-luxury-200">
                      <span className="font-semibold text-luxury-900">
                        Total to Pay:
                      </span>
                      <span className="text-lg sm:text-xl font-bold gradient-text">
                        ₦{parseInt(amount).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 p-4 bg-blue-50 rounded-xl mb-6">
                  <FiShield className="w-5 h-5 text-blue-500 shrink-0" />
                  <p className="text-xs sm:text-sm text-blue-700">
                    Your transaction is secure and encrypted.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setStep(1)}
                    className="flex-1 py-3 px-4 border border-luxury-300 text-luxury-600 rounded-xl hover:bg-luxury-100 transition-colors text-sm sm:text-base font-semibold"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={isLoading || !selectedMethod}
                    className="flex-1 py-3 px-4 bg-linear-to-r from-primary-600 to-secondary-600 text-white rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm sm:text-base font-semibold flex items-center justify-center"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FiLock className="inline mr-2" />
                        Complete Deposit
                      </>
                    )}
                  </button>
                </div>
              </GlassCard>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
};
